import { EditOutlined } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Button, Card, CardContent, CardHeader, Dialog, DialogActions, DialogTitle, FormControlLabel, FormGroup, IconButton, Input, InputAdornment, Switch, Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import errorHandler from "../../utils/errorHandler";
import { capitalize } from "../../utils/helpers";
import DeleteIcon from "@mui/icons-material/Delete";
import ViewEmailDetail from "./ViewEmail";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import { emailServices } from "../../services/AllServices";
import { Loader } from "../../components/CustomLoader";
import TableFilter from "../../components/shared/TableFilter";
import { STATUS_OPTIONS_LIST } from "../../utils/constants";

export default function EmailTemplateList() {
  const navigate = useNavigate();
  const [searchItem, setSearchItem] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(""); // For debounced search value
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState();
  const [warning, setWarning] = useState("");
  const [action, setAction] = useState("");
  const [show, setShow] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusId, setStatusId] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [status, setStatus] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [viewId, setViewId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false); 

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleClose = () => setShow(false);

  const columns = [
    {
      name: "S.No.",
      sortable: false,
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      name: "Title",
      sortable: true,
      selector: (row) => capitalize(row.title),
    },
    {
      name: "Status",
      cell: (row) => (
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={row.status === "active"}
                onClick={() => {
                  setShow(true);
                  setCurrentStatus(row.status);
                  setStatusId(row._id);
                  setAction("status");
                  setWarning(
                    `Are you sure you want to ${
                      row.status === "active" ? "inactive" : "activate"
                    } the email-template?`
                  );
                }}
                inputProps={{ "aria-label": "controlled" }}
              />
            }
          />
        </FormGroup>
      ),
    },
    {
      name: "Action",
      center: true,
      cell: (row) => (
        <>
          <Tooltip title="Edit" placement="left">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/email-template/edit/${row?._id}`);
              }}
            >
              <EditOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="View" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                setModalShow(true);
                setViewId(row._id);
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete" placement="right">
            <IconButton color="primary" onClick={() => deleteData(row?._id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  const deleteData = async (id) => {
    Swal.fire({
      text: "Are you sure you want to delete this Template?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await emailServices.deleteEmail({ id });
          if (res?.status === 200) {
            toast.success(res?.data?.message, { toastId: "success" });
            setData((prevData) => prevData.filter((item) => item._id !== id));
            setTotalPages((prevTotal) => prevTotal - 1);
            setRefresh(!refresh);
          } else {
            toast.error(res?.data?.message, { toastId: "error" });
          }
        } catch (error) {
          console.error("Error deleting...", error);
          errorHandler(error);
        }
      }
    });
  };


  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setDebouncedSearch(searchItem); 
    }, 500); 

    return () => clearTimeout(delayDebounceFn); 
  }, [searchItem]);

  useEffect(() => {
    const body = {
      searchItem: debouncedSearch, 
      pageNumber: currentPage,
      pageSize: pageSize,
      status: status,
    };
    setIsLoading(true)
    setLoaderDelay(false); 
    emailServices.getEmail(body)
      .then((res) => {
        if (res.status === 200) {
          setTimeout(() => {
            setData(res?.data?.data?.data);
            setTotalPages(res?.data?.data?.totalItems);
            setLoaderDelay(true); 
          }, 500);
        }
      })
      .catch((err) => {
        setIsLoading(false)
        errorHandler(err);
        setLoaderDelay(true); 
      }) 
      .finally(() => {
        setIsLoading(false);
        setLoaderDelay(false);
      });
  }, [debouncedSearch, currentPage, pageSize, refresh, status]);

  const toggleChecked = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === "active" ? "inactive" : "active";

      const res = await emailServices.changeStatusEmail({
        id,
        status: newStatus,
      });

      setCurrentStatus("");

      if (res.status === 200 && res?.data?.message) {
        toast.success(res?.data?.message, { toastId: "success" });
        setRefresh(!refresh);
        setShow(false);
      } else {
        toast.error(res?.data?.message, { toastId: "error1" });
        setShow(false);
      }
    } catch (err) {
      setShow(false);
      errorHandler(err);
    }
  };

  return (
    <Card>
      <CardHeader
        titleTypographyProps={{ variant: "h4" }}
        title="Email Template"
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <TableFilter
          data={STATUS_OPTIONS_LIST}
          value={status}
          setValue={setStatus}
          filterName="Status"
        />
        <Input
          label="Search"
          variant="standard"
          value={searchItem}
          placeholder="Search"
          sx={{ ml: 3, mr: 3 }}
          onChange={(e) => setSearchItem(e.target.value)}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="clear-search"
                onClick={() => {
                  setSearchItem("");
                }}
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          }
        />

      </Box>
        <CardContent>
          <DataTable
            customStyles={{
              headCells: {
                style: {
                  fontSize: "15px",
                  fontWeight: "bold",
                },
              },
            }}
            columns={columns}
            data={data}
            pagination
            paginationServer
            paginationTotalRows={totalPages}
            onChangePage={handlePageChange}
            paginationPerPage={pageSize}
            paginationRowsPerPageOptions={[10, 20, 50]}
            onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
              setPageSize(currentRowsPerPage);
              setCurrentPage(currentPage);
            }}
            progressPending={isLoading || !loaderDelay}
            progressComponent={<Loader />}
            className="custom"
          />
        </CardContent>
        <Dialog open={show} onClose={() => setShow(false)} className="popupbox-statis">
          <DialogTitle>{warning}</DialogTitle>
          <DialogActions className="status-box-action">
            <Button
            variant="outlined"
              onClick={() => {
                toggleChecked(statusId, currentStatus);
                setShow(false);
              }}
              color="primary"
            >
              Yes
            </Button>
            <Button  variant="outlined" onClick={() => setShow(false)} color="primary">
              No
            </Button>
          </DialogActions>
        </Dialog> 

      {modalShow ? (
        <ViewEmailDetail
          show={modalShow}
          onHide={() => setModalShow(false)}
          id={viewId}
        />
      ) : (
        ""
      )}
    </Card>
  );
}
